import EventDispatcher from "../../../../util/EventDispatcher";
import { isNull } from "../../../../util/TypeUtil";
import { getURLParameter } from "../../../../util/URLUtil";
import { DialogOption } from "../../engine/model/Dialog";
import DialogPlayer from "../../engine/player/DialogPlayer";
import DialogPlayerEvent from "../../engine/player/DialogPlayerEvent";
import DefaultDialogPlayerCommand from "./DefaultDialogPlayerCommand";
import DefaultDialogPlayerEvent from "./DefaultDialogPlayerEvent";
import PlayMode from "./PlayMode";

    export default class DefaultDialogPlayer extends EventDispatcher {
        constructor(timeTickInterval = 100) {
            super();
            
            this.timeTickInterval = timeTickInterval;
            this.dialogPlayer = new DialogPlayer(timeTickInterval);
            this.scores = {};
            this.delayTimeLeft = -1;
            this.autoTakeTimeLeft = -1;
            this._playMode = PlayMode.AUTO;
            this._stepAvailable = false;

            this.currentDialog = null;
            this.delayTimer = null;
            this.delayTimerDelay = 0;
            this.delayStartTime = null;
            this.autoTakeTimer = null;
            this.autoTakeTimerDelay = 0;
            this.autoTakeTickTimer = null;
            this.autoTakeStartTime = null;
            this.autoTakeOption = null;

            this.dialogPlayer.addEventListener(DialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.START_DIALOG, this.handleStartDialog, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.ENTER_DIALOG_STATE, this.handleEnterDialogState, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.ENTER_DIALOG_TRANSITION, this.handleEnterDialogTransition, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.EXIT_DIALOG_TRANSITION, this.handleExitDialogTransition, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.END_DIALOG, this.handleEndDialog, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick, this);
            this.dialogPlayer.addEventListener(DialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleCommandSequenceEnd, this);

        }

        destroy() {
            // Remove event listeners
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.DIALOG_COMMAND, this.handleDialogCommand);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.DIALOG_OPTION, this.handleDialogOption);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.START_DIALOG, this.handleStartDialog);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.ENTER_DIALOG_STATE, this.handleEnterDialogState);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.EXIT_DIALOG_STATE, this.handleExitDialogState);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.ENTER_DIALOG_TRANSITION, this.handleEnterDialogTransition);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.EXIT_DIALOG_TRANSITION, this.handleExitDialogTransition);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.END_DIALOG, this.handleEndDialog);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.DIALOG_TIME_OUT, this.handleDialogTimeOut);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.SKIP_DIALOG_COMMAND, this.handleSkipDialogCommand);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.SKIP_DIALOG_OPTION, this.handleSkipDialogOption);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.DIALOG_TIME_TICK, this.handleDialogTimeTick);
            this.dialogPlayer.removeEventListener(DialogPlayerEvent.COMMAND_SEQUENCE_END, this.handleCommandSequenceEnd);

            // Clear references
            this.dialogPlayer.destroy();
            this.clearDelayTimer();
            this.clearAutoTake();
        }

        getMaxTime() {
            return this.dialogPlayer.getMaxTime();
        }

        getCurrentCommandSequence() {
            return this.dialogPlayer.getCurrentCommandSequence();
        }

        getPlayMode() {
            return this._playMode;
        }

        setPlayMode(value) {
            this._playMode = value;
        }

        getStepAvailable() {
            return this._stepAvailable;
        }

        setDialog(value, conditionSolver) {
            this.currentDialog = value;
            this.clearDelayTimer();
            this.clearAutoTake();
            this.dialogPlayer.setDialog(value, conditionSolver);
            this._stepAvailable = true;
            this.scores = {};
        }

        next() {
            this._stepAvailable = false;
            this.dialogPlayer.next();
        }

        takeOption(option) {
            this.clearAutoTake();
            this.dialogPlayer.takeOption(option);
        }

        pauseTimers() {
            let timeLeft;
            let delay;
            
            this.dialogPlayer.pauseDialogTime();
            if (! isNull(this.delayTimer) && this.delayTimeLeft === -1)
            {
                
                timeLeft = this.delayTimerDelay - (this.getTimer() - this.delayStartTime);
                this.clearDelayTimer();
                this.delayStartTime = this.delayTimerDelay;
                this.delayTimeLeft = timeLeft;
            }
            if (! isNull(this.autoTakeTimer) && this.autoTakeTimeLeft === -1)
            {
                timeLeft = this.autoTakeTimerDelay - (this.getTimer() - this.autoTakeStartTime);
                const autoTakeOption = this.autoTakeOption;
                this.clearAutoTake();
                this.autoTakeOption = autoTakeOption;
                this.autoTakeStartTime = this.autoTakeTimerDelay;
                this.autoTakeTimeLeft = timeLeft;
            }
        }

        resumeTimers() {
            if (this.delayTimeLeft !== -1)
            {
                // Mind: delayStartTime was used to temporarily store the original delay!!
                this.delayStartTime = this.getTimer() - (this.delayStartTime - this.delayTimeLeft);
                this.delayTimer = setTimeout(this.handleDelayTimerComplete.bind(this), this.delayTimeLeft);            
                this.delayTimeLeft = -1;
            }
            if (this.autoTakeTimeLeft !== -1)
            {
                // Mind: autoTakeStartTime was used to temporarily store the original autotake delay!!
                this.autoTakeStartTime = this.getTimer() - (this.autoTakeStartTime - this.autoTakeTimeLeft);
                this.autoTakeTimer = setTimeout(this.handleAutoTakeTimerComplete.bind(this), this.autoTakeTimeLeft);

                this.autoTakeTickTimer = setInterval(this.handleAutoTakeTickTimer.bind(this), this.timeTickInterval);
                this.autoTakeTimeLeft = -1;
            }
            this.dialogPlayer.resumeDialogTime();
        }

        reset() {
            this.dialogPlayer.reset();
        }

        getScores() {
            const scores = {};

            for (const variableName in this.scores) {
                scores[variableName] = this.scores[variableName];
            }
            return scores;
        }

        getScore(variableName) {
            return (isNull(this.scores[variableName])) ? 0 : this.scores[variableName];
        }

        clearScores() {
            this.scores = {};
        }

        clearScore(variableName) {
            this.scores[variableName] = null;
        }

        getCurrentCommandSequenceDuration() {
            const currentCommandSequence = this.dialogPlayer.getCurrentCommandSequence();
            let duration = 0;
            const commandsIterator = currentCommandSequence.getCommandsIterator();
            let command;
            let defaultDialogPlayercommand;

            while (commandsIterator.hasNext()) {
                command = commandsIterator.next();
                if (command.data instanceof DefaultDialogPlayerCommand) {
                    defaultDialogPlayerCommand = command.data;
                    if (defaultDialogPlayerCommand.command === DefaultDialogPlayerCommand.DELAY) {
                        duration += defaultDialogPlayerCommand.getData();
                    }
                }
            }
            return duration;
        }

        // -- Non-public ----------------------------

        getTimer() {
            return new Date().getTime();
        }

        clearDelayTimer() {
            if (! isNull(this.delayTimer)) {
                clearTimeout(this.delayTimer);
                this.delayStartTime = -1;
                this.delayTimer = null;
                this.delayTimeLeft = -1;
            }
        }

        setAutoTake(option) {
            const delay = option.getAutoTake();
            
            if (isNull(this.autoTakeTimer) || delay < (this.autoTakeTimerDelay - (this.getTimer() - this.autoTakeStartTime))) {
                this.clearAutoTake();
                this.autoTakeTimer = setTimeout(this.handleAutoTakeTimerComplete.bind(this), delay);
                this.autoTakeTimerDelay = delay;
                this.autoTakeStartTime = this.getTimer();
                this.autoTakeOption = option;
                
                this.autoTakeTickTimer = setInterval(this.handleAutoTakeTickTimer.bind(this), this.timeTickInterval);
            }
        }

        clearAutoTake() {
            if (! isNull(this.autoTakeTimer)) {

                clearInterval(this.autoTakeTickTimer);            
                this.autoTakeTickTimer = null;

                clearTimeout(this.autoTakeTimer);
                this.autoTakeTimer = null;            
                this.autoTakeOption = null;
            }
        }

        checkNext(lastEventType) {
            this._stepAvailable = true;
            switch (this._playMode) {
                case PlayMode.AUTO:
                    this.next();
                    break;

                case PlayMode.COMMANDS:
                    switch (lastEventType)
                    {
                        case DefaultDialogPlayerEvent.START_DIALOG:
                        case DefaultDialogPlayerEvent.ENTER_DIALOG_STATE:
                        case DefaultDialogPlayerEvent.EXIT_DIALOG_STATE:
                        case DefaultDialogPlayerEvent.ENTER_DIALOG_TRANSITION:
                        case DefaultDialogPlayerEvent.EXIT_DIALOG_TRANSITION:
                        case DefaultDialogPlayerEvent.DIALOG_TIME_OUT:
                            this.next();
                            break;
                    }
                    break;

                case PlayMode.MANUAL:
                    break;
            }
        }

        processCommand(command) {
            let defaultDialogPlayerCommand;
            let scoreString;
            let symbolIndex;
            let multiplier;
            let variableName;
            let score;

            if (command instanceof DefaultDialogPlayerCommand) {
                defaultDialogPlayerCommand = command;
                switch (defaultDialogPlayerCommand.command)
                {
                    case DefaultDialogPlayerCommand.SCORE:
                        const scoreMutations = defaultDialogPlayerCommand.getData();

                        for (let i = 0; i < scoreMutations.length; i++) {
                            const scoreMutation = scoreMutations[i];
                            if (!this.scores[scoreMutation.scoreName]) {
                                this.scores[scoreMutation.scoreName] = 0;
                            }

                            switch (scoreMutation.mutationType) {
                                case scoreMutation.SET:
                                    this.scores[scoreMutation.scoreName] = scoreMutation.value;
                                    break;
                                case ScoreMutation.ADD:
                                    this.scores[scoreMutation.scoreName] += scoreMutation.value;
                                    break;
                                case ScoreMutation.SUBTRACT:
                                    this.scores[scoreMutation.scoreName] -= scoreMutation.value;
                                    break;
                            }

                            this.dispatchEvent(DefaultDialogPlayerEvent.SCORE, scoreMutation.scoreName);

                        }

                        this.checkNext(DefaultDialogPlayerEvent.SCORE);
                        break;
                    case DefaultDialogPlayerCommand.DELAY:
                        this.clearDelayTimer();
                        this.delayStartTime = this.getTimer();
                        const sf = parseFloat(getURLParameter("speed"));
                        const speedFactor = isNaN(sf) ? 1 : sf;
                        this.delayTimer = setTimeout(this.handleDelayTimerComplete.bind(this), defaultDialogPlayerCommand.getData() / speedFactor);
                        this.delayTimerDelay = defaultDialogPlayerCommand.getData();
                        break;
                    case DefaultDialogPlayerCommand.CONTENT:
                        this.dispatchEvent(DefaultDialogPlayerEvent.CONTENT, defaultDialogPlayerCommand.getData());
                        this.checkNext(DefaultDialogPlayerEvent.CONTENT);
                        break;
                    case DefaultDialogPlayerCommand.COMMAND:
                        this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_COMMAND, defaultDialogPlayerCommand.getData());
                        this.checkNext(DefaultDialogPlayerEvent.DIALOG_COMMAND);
                        break;
                    default:
                        this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_COMMAND, defaultDialogPlayerCommand);
                        this.checkNext(DefaultDialogPlayerEvent.DIALOG_COMMAND);
                        break;

                }
            } else {
                this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_COMMAND, command);
                this.checkNext(DefaultDialogPlayerEvent.DIALOG_COMMAND);
            }
        }

        processOption(command, option) {
            let defaultDialogPlayerCommand;
            let scoreString;
            let symbolIndex;
            let multiplier;
            let variableName;
            let score;

            if (option.getAutoTake() !== -1) {
                this.setAutoTake(option);
            }
            if (command instanceof DefaultDialogPlayerCommand) {
                defaultDialogPlayerCommand = command;
                if (defaultDialogPlayerCommand.command === DefaultDialogPlayerCommand.CONTENT)
                {
                    this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_OPTION, {"data": defaultDialogPlayerCommand.getData(), "option": option});
                    this.checkNext(DefaultDialogPlayerEvent.DIALOG_OPTION);
                }
                else
                {
                    throw("Illegal DialogOption!! Options can only contain CONTENT commands!");
                }
            }
            else
            {
                this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_OPTION, {"data": command, "option": option});
                this.checkNext(DefaultDialogPlayerEvent.DIALOG_OPTION);
            }
        }

        // ---- Event handlers -------------------------------

        handleDialogCommand({data}) {
            this.processCommand(data.data);
        }

        handleDialogOption({data}) {
            this.processOption(data.data, data.option);
        }

        handleStartDialog(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.START_DIALOG);
            this.checkNext(DefaultDialogPlayerEvent.START_DIALOG);
        }

        handleEndDialog(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.END_DIALOG);
        }

        handleCommandSequenceEnd(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.COMMAND_SEQUENCE_END);
        }

        handleEnterDialogState(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.ENTER_DIALOG_STATE);
            this.checkNext(DefaultDialogPlayerEvent.ENTER_DIALOG_STATE);
        }

        handleExitDialogState(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE);
            this.checkNext(DefaultDialogPlayerEvent.EXIT_DIALOG_STATE);
        }

        handleEnterDialogTransition(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.ENTER_DIALOG_TRANSITION);
            this.checkNext(DefaultDialogPlayerEvent.ENTER_DIALOG_TRANSITION);
        }

        handleExitDialogTransition(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.EXIT_DIALOG_TRANSITION);
            this.checkNext(DefaultDialogPlayerEvent.EXIT_DIALOG_TRANSITION);
        }

        handleDialogTimeOut(dialogPlayerEvent) {
            this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_TIME_OUT, new DialogOption(this.currentDialog.getTimeOutState()));
        }

        handleDialogTimeTick({data}) {
            this.dispatchEvent(DefaultDialogPlayerEvent.DIALOG_TIME_TICK, data, this);
        }

        handleDelayTimerComplete() {
            clearTimeout(this.delayTimer);
            this.delayTimer = null;
            this._stepAvailable = true;
            this.next();
        }

        handleAutoTakeTimerComplete() {
            this.dispatchEvent(DefaultDialogPlayerEvent.AUTO_TAKE_OPTION_TAKEN, {option: this.autoTakeOption});
            
            this.takeOption(this.autoTakeOption);
            //this.clearAutoTake();
        }

        handleAutoTakeTickTimer() {
            
            const timePassed = this.getTimer() - this.autoTakeStartTime;
            const timeRemaining = this.autoTakeOption.getAutoTake() - timePassed;
    //        console.log("autotake tick timer. Passed " + timePassed + " remaining " + timeRemaining);
            this.dispatchEvent(DefaultDialogPlayerEvent.AUTO_TAKE_TIME_TICK, {"passed": timePassed, "remaining": timeRemaining});
        }
    }

